import React, { useCallback } from "react";
import { linkVertical, scaleLog, extent } from "d3";

import { useStore } from "../../store";
import { formatTimeReadableDayMonthYear, isSmallViewport } from "../../utils";
import narrative from "./narratives";
import { Timeline } from "./Timeline";
import "./Controls.scss";

export const defaultTooltip = {
  text: null,
  x: 0,
  y: 0,
};

export const Controls = ({ tooltip = defaultTooltip }) => {
  const hideChapterNames = isSmallViewport();
  const dispatch = useStore(useCallback((state) => state.dispatch, []));
  const narrativeIdx = useStore(
    useCallback((state) => state.app.corporate.narrativeIdx, [])
  );

  return (
    <>
      <div className="corporate_snapshot__controls relative flex flex-wrap col-12">
        <Timeline
          data={narrative}
          hideChapterNames={hideChapterNames}
          selectedIdx={narrativeIdx}
          onChapterChange={(narrativeIdx) => {
            dispatch({
              type: `CORPORATE_UPDATE_NARRATIVE_IDX`,
              payload: narrativeIdx,
            });
          }}
        />
        <Tooltip
          className="absolute"
          edges={tooltip.edges}
          company={tooltip.company}
          country={tooltip.country}
          incorporated={tooltip.incorporated}
          notes={tooltip.notes}
          text={tooltip.text}
          x={tooltip.x}
          y={tooltip.y}
        />
      </div>
    </>
  );
};

const Tooltip = ({
  edges = [],
  company = null,
  country = null,
  incorporated = null,
  text = null,
  notes = null,
  x = 0,
  y = 0,
  className = "",
  children = null,
}) => {
  const showEdgeGraph = edges.length > 0;

  const belowHalfScreen = y >= window.innerHeight / 2;

  return (
    company && (
      <div
        className={`tooltip fade-in block-font ml2 p2 rounded border ${className}`}
        style={{
          left: x,
          top: belowHalfScreen ? y - 200 : y,
        }}
        // style={{ style }}
      >
        {/* {children} */}
        {showEdgeGraph && (
          <>
            <div className="bold">Partially Owned by</div>
            <PartialOwnershipGraph edges={edges} />
          </>
        )}
        {company && <div className="bold mb1">{company}</div>}
        <div className="color-gray">
          {country && <div className="mt1">Incorporated in {country}</div>}
          {incorporated && (
            <div className="flex flex-column mb1">
              ({formatTimeReadableDayMonthYear(new Date(incorporated))})
            </div>
          )}
        </div>
        {text && <div className="tooltip__notes">{text}</div>}
        <div className="color-gray">
          {notes && <div className="italic">{notes}</div>}
        </div>
      </div>
    )
  );
};

const PartialOwnershipGraph = ({ edges = [] }) => {
  const link = linkVertical()
    .x((d) => d.x)
    .y((d) => d.y);

  const maxRadius = 30;
  const segmentWidth = 130;
  const svgHeight = 150;

  const r = scaleLog()
    .domain(extent(edges, (e) => e?.percentage || 0.1))
    .range([10, maxRadius]);

  return (
    <svg
      width={edges.length * segmentWidth}
      height={svgHeight}
      viewBox={`0 0 ${edges.length * segmentWidth} ${svgHeight}`}
    >
      {edges.map((e, eIdx) => {
        const radius = r(e?.percentage || 0.1);
        return (
          <g
            key={`partial-ownership-tooltip-graph-${e.target}-${eIdx}`}
            transform={`translate(${eIdx * segmentWidth}, 50)`}
          >
            {e.type === "Individual" && (
              <rect
                // transform={`translate()`}
                x={segmentWidth / 2 - radius / 2}
                y={0}
                width={radius}
                height={radius}
                stroke={`white`}
                strokeWidth={2}
                strokeOpacity={0.6}
                fill="none"
              />
            )}
            {e.type === "Company" && (
              <circle
                cx={segmentWidth / 2}
                cy={radius}
                r={radius}
                stroke={`white`}
                strokeWidth={2}
                strokeOpacity={0.6}
                fill="none"
              />
            )}
            <path
              d={link({
                source: { x: segmentWidth / 2, y: 0 },
                target: { x: -(eIdx * segmentWidth) + 50, y: -40 },
              })}
              fill="none"
              stroke="white"
              strokeWidth={1}
              opacity={0.6}
            />

            <text
              textAnchor="middle"
              x={segmentWidth / 2}
              y={75}
              fontSize={10}
              fill={`white`}
            >
              {e.source}
            </text>
          </g>
        );
      })}
    </svg>
  );
};
